import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

const MICROSOFT_SCOPES = ['User.Read', 'profile', 'email', 'openid'];
const POPUP_BLOCKED_CODES = ['empty_window_error', 'popup_window_error'];
const INTERACTION_IN_PROGRESS_CODE = 'interaction_in_progress';

const ERROR_MESSAGES = {
  interaction_in_progress:
    'Hay un inicio de sesión de Microsoft en curso. Cierra las ventanas anteriores e inténtalo de nuevo.',
  empty_window_error:
    'Tu navegador bloqueó la ventana de inicio de sesión de Microsoft. Habilita las ventanas emergentes para este sitio.',
  popup_window_error:
    'Tu navegador bloqueó la ventana de inicio de sesión de Microsoft. Habilita las ventanas emergentes para este sitio.',
  access_denied: 'Microsoft rechazó el acceso. Contacta a soporte.',
  login_required: 'Debes iniciar sesión con Microsoft para continuar.',
  interaction_required: 'Microsoft requiere una acción adicional para iniciar sesión.',
};

const getErrorCode = (error) => error?.errorCode || error?.name || 'unknown_error';

const getErrorMessage = (error) => {
  const code = getErrorCode(error);
  return ERROR_MESSAGES[code] || `Error al conectar con Microsoft: ${error?.message || code}`;
};

const clearInteractionStatus = (instance) => {
  try {
    const clientId = instance.getConfiguration?.().auth?.clientId;
    if (!clientId) return;
    const key = `msal.${clientId}.interaction.status`;
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  } catch (e) {
    console.warn('⚠️ No se pudo limpiar interaction_in_progress:', e);
  }
};

export const MicrosoftLoginButton = ({ onSuccess, onError, text, lang }) => {
  const { instance } = useMsal();

  const completeLogin = async (loginResponse) => {
    const account = loginResponse.account;

    console.log('✅ Usuario autenticado con Microsoft:', {
      username: account?.username,
      name: account?.name,
    });

    // Enviar al backend PHP de Moodle
    const response = await fetch(
      'https://admin.aprendersteam.com/local/tipddy/services/microsoft_auth.php',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: account.username, // Email del usuario
          name: account.name,
          microsoft_id: account.localAccountId,
          access_token: loginResponse.accessToken,
        }),
      }
    );

    const raw = await response.text();
    console.log('🔵 Respuesta cruda microsoft_auth.php:', response.status, raw);

    let data;
    try {
      data = JSON.parse(raw);
    } catch (parseError) {
      throw new Error(`Respuesta no válida del servidor (HTTP ${response.status})`);
    }

    if (data.success) {
      console.log('✅ Login exitoso:', data);
      onSuccess({
        key: data.userkey,
        username: data.username,
        token: data.token,
        userid: data.userid,
      });
    } else {
      console.error('❌ Error del servidor:', data.error);
      onError(data.error || 'Error al autenticar con Microsoft');
    }
  };

  const startRedirect = () => {
    console.warn('🔁 Popup bloqueado: fallback a loginRedirect');
    return instance.loginRedirect({
      scopes: MICROSOFT_SCOPES,
      prompt: 'select_account',
    });
  };

  // Procesa la respuesta cuando el navegador vuelve del loginRedirect (fallback)
  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((loginResponse) => {
        if (!loginResponse) return;
        completeLogin(loginResponse).catch((error) => {
          console.error('❌ Error procesando redirect Microsoft:', getErrorCode(error), error);
          onError(getErrorMessage(error));
        });
      })
      .catch((error) => {
        const code = getErrorCode(error);
        console.error('❌ Error en handleRedirectPromise:', code, error);
        if (code === INTERACTION_IN_PROGRESS_CODE) {
          clearInteractionStatus(instance);
          return;
        }
        onError(getErrorMessage(error));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    try {
      // Iniciar login con Microsoft
      const loginResponse = await instance.loginPopup({
        scopes: MICROSOFT_SCOPES,
        prompt: 'select_account',
      });

      await completeLogin(loginResponse);
    } catch (error) {
      const code = getErrorCode(error);
      console.error('❌ Error en login Microsoft:', code, error);

      // Popup bloqueado por el navegador -> continuar con redirect
      if (POPUP_BLOCKED_CODES.includes(code)) {
        try {
          await startRedirect();
        } catch (redirectError) {
          console.error('❌ Error en fallback loginRedirect:', getErrorCode(redirectError), redirectError);
          onError(getErrorMessage(redirectError));
        }
        return;
      }

      // Estado MSAL colgado -> limpiar cache y reintentar
      if (code === INTERACTION_IN_PROGRESS_CODE) {
        console.warn('⚠️ interaction_in_progress: limpiando cache y reintentando');
        clearInteractionStatus(instance);
        try {
          const retryResponse = await instance.loginPopup({
            scopes: MICROSOFT_SCOPES,
            prompt: 'select_account',
          });
          await completeLogin(retryResponse);
        } catch (retryError) {
          console.error('❌ Error en reintento Microsoft:', getErrorCode(retryError), retryError);
          onError(getErrorMessage(retryError));
        }
        return;
      }

      onError(getErrorMessage(error));
    }
  };

  return (
    <button
      onClick={handleLogin}
      type="button"
      className="w-[280px] flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg border border-gray-300 transition-colors shadow-sm hover:shadow"
      style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      {/* Logo de Microsoft */}
      <svg className="w-5 h-5" viewBox="0 0 21 21">
        <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
        <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
        <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
      </svg>
      
      <span>{text}</span>
    </button>
  );
};
